import { rescoreApprovalProblem, type RescoreSummary } from '@cyberscore/contracts';
import { ArrowRight, Check, RefreshCw, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href } from '../../lib/router';
import { DataView, type Column } from '../../ui/DataView';
import { useToast } from '../../ui/Toasts';
import { bandLabel, bandTone, signed } from '../portfolio/format';
import {
  useRescore,
  useRescoreAccess,
  useRescoreAction,
  usePendingRescore,
  useRescorePreview,
  type Rescore,
  type RescoreItem,
} from './api';

const STATUS: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Waiting for approval', tone: 'warn' },
  applied: { label: 'Applied', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'bad' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};
const when = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); // prettier-ignore

/**
 * Re-scores (task 44): assessments keep the configuration they started on until a re-score moves them to
 * the live one. Proposed with a dry run of every score it changes; applied once two other people approve.
 */
const SPEC: ListSpec = { id: 'rescores', sort: 'requested', filters: ['status'] };

export function RescoresPage() {
  const access = useRescoreAccess();
  const pending = usePendingRescore();
  const list = useListState(SPEC);
  const past = usePage<Rescore>('/rescores', list.state, 'decided=yes');
  const [open, setOpen] = useState<string | null>(null);
  const columns: Column<Rescore>[] = [
    {
      key: 'requested',
      label: 'Proposed',
      sort: 'requested',
      render: (r) => <span className="muted">{when(r.requested_at)}</span>,
    },
    { key: 'note', label: 'Note', render: (r) => <span className="cell-title">{r.note}</span> },
    {
      key: 'assessments',
      label: 'Assessments',
      align: 'right',
      render: (r) => <span className="num">{r.summary.assessments}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <span className={`pill ${STATUS[r.status]!.tone}`}>{STATUS[r.status]!.label}</span>,
    },
    {
      key: 'decided',
      label: 'Decided by',
      wide: true,
      render: (r) => <span className="muted">{r.decided_by_name ?? '–'}</span>,
    },
    { key: 'open', label: '', align: 'right', render: (r) => <button type="button" className="btn btn-sm btn-ghost" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'Details'}</button> }, // prettier-ignore
  ];

  return (
    <>
      <div className="page-head">
        <h1>Re-scores</h1>
        <p>
          An assessment keeps the configuration it started on. A re-score moves every open assessment to the live configuration, after a dry run and two approvals from people other than the one who proposed it.
        </p>
      </div>
      {pending.isPending ? (
        <div className="empty">Loading…</div>
      ) : pending.isError ? (
        <div className="empty">{pending.error.message}</div>
      ) : pending.data ? (
        <Proposal id={pending.data.id} />
      ) : (
        access.acts && <Propose />
      )}

      {(list.filtered || !!past.data?.total) && (
        <section className="mt">
          <h2 className="section-title">Earlier re-scores</h2>
          <DataView
            list={list}
            query={past}
            noun="re-scores"
            rowKey={(r) => r.id}
            columns={columns}
            filters={[{ key: 'status', label: 'Outcomes', options: (['applied', 'rejected', 'withdrawn'] as const).map((st) => [st, STATUS[st]!.label] as const) }]}
            sorts={[{ value: 'requested', label: 'Proposed', dir: 'desc' }]}
            searchLabel="Search notes"
            empty={<><RefreshCw size={28} /><strong>No earlier re-scores</strong></>}
          />
          {open && <section className="card mt"><Proposal id={open} embedded /></section>}
        </section>
      )}
    </>
  ); // prettier-ignore
}

function Propose() {
  const preview = useRescorePreview();
  const act = useRescoreAction();
  const toast = useToast();
  const [note, setNote] = useState('');
  const data = preview.data;
  return (
    <section className="card pad">
      <h2 className="section-title">Check the book against the live configuration</h2>
      <p className="muted small">The dry run changes nothing. It shows what each open assessment would score on the configuration a new one would get today.</p>
      <button type="button" className="btn" onClick={() => preview.mutate()} disabled={preview.isPending}>
        <RefreshCw size={15} /> {preview.isPending ? 'Running…' : data ? 'Run again' : 'Run the dry run'}
      </button>
      {preview.isError && <div className="error mt" role="alert">{preview.error.message}</div>}
      {data && (
        <div className="mt">
          {data.items.length === 0 ? (
            <p className="ok-line">Every open assessment is already on the live configuration.</p>
          ) : (
            <>
              <Summary s={data.summary} />
              <Items items={data.items} />
              <form
                className="row gap mt wrap"
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await act.mutateAsync({ type: 'propose', note: note.trim() });
                    toast('Proposed. Two other people need to approve it.', 'ok');
                  } catch (err) {
                    toast(err instanceof Error ? err.message : 'Could not propose it', 'bad');
                  }
                }}
              >
                <input className="input grow" aria-label="Why re-score now" placeholder="Why now, e.g. “Platform version 7 changes MFA scoring”" value={note} onChange={(e) => setNote(e.target.value)} />
                <button type="submit" className="btn btn-primary" disabled={!note.trim() || act.isPending}>
                  Propose this re-score
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </section>
  ); // prettier-ignore
}

function Proposal({ id, embedded }: { id: string; embedded?: boolean }) {
  const r = useRescore(id);
  const access = useRescoreAccess();
  const act = useRescoreAction();
  const toast = useToast();
  const [note, setNote] = useState('');
  if (r.isPending) return <div className="empty compact">Loading…</div>;
  if (r.isError) return <div className="empty compact">{r.error.message}</div>;
  const d = r.data;
  const problem = access.acts
    ? rescoreApprovalProblem(d, { id: access.userId, permissions: access.permissions })
    : null;
  const mine = d.requested_by === access.userId;
  const run = async (a: Parameters<typeof act.mutateAsync>[0], done: string) => {
    try {
      await act.mutateAsync(a);
      setNote('');
      toast(done, 'ok');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not do that', 'bad');
    }
  };
  const applied = d.items.filter((i) => i.outcome === 'applied').length;
  const skipped = d.items.filter((i) => i.outcome === 'skipped').length;

  return (
    <section className={embedded ? 'pad' : 'card pad'}>
      <div className="row-between wrap gap">
        <div>
          <h2 className="section-title">{d.note}</h2>
          <p className="muted small">
            Proposed by {d.requested_by_name ?? 'someone'} on {when(d.requested_at)} · dry run as on {d.as_of}
            {d.status === 'applied' && ` · ${applied} moved${skipped ? `, ${skipped} skipped` : ''}`}
            {d.decision_note && ` · “${d.decision_note}”`}
          </p>
        </div>
        <span className={`pill ${STATUS[d.status]!.tone}`}>{STATUS[d.status]!.label}</span>
      </div>
      <Summary s={d.summary} />
      <ol className="approvals" aria-label="Approvals">
        {Array.from({ length: d.needed }, (_, n) => d.approvals[n]).map((p, n) => (
          <li key={n} className={p ? 'done' : undefined}>
            {p ? <Check size={14} className="ok-t" /> : <span className="dot" />}
            {p ? `${p.approver_name ?? 'Approved'}${p.note ? ` · “${p.note}”` : ''}` : `Approval ${n + 1} of ${d.needed}`}
          </li>
        ))}
      </ol>
      <Items items={d.items} />
      {d.status === 'pending' && access.acts && (
        <div className="row gap mt wrap">
          {!mine && (
            <input className="input grow" aria-label="Note" placeholder="Note (needed to reject)" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
          {!mine && (
            <button type="button" className="btn" disabled={!note.trim() || act.isPending} onClick={() => run({ type: 'reject', id, note: note.trim() }, 'Rejected')}>
              <X size={15} /> Reject
            </button>
          )}
          {mine ? (
            <button type="button" className="btn" disabled={act.isPending} onClick={() => run({ type: 'withdraw', id }, 'Withdrawn')}>
              <Undo2 size={15} /> Withdraw
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={!!problem || act.isPending} title={problem ?? undefined} onClick={() => run({ type: 'approve', id, note: note.trim() || undefined }, d.approvals.length + 1 >= d.needed ? 'Approved and applied' : 'Approved. One more approval applies it.')}>
              <Check size={15} /> {d.approvals.length + 1 >= d.needed ? 'Approve and apply' : 'Approve'}
            </button>
          )}
          {problem && !mine && <span className="muted small">{problem}</span>}
          {mine && <span className="muted small">Two other people must approve it.</span>}
        </div>
      )}
    </section>
  ); // prettier-ignore
}

function Summary({ s }: { s: RescoreSummary }) {
  return (
    <div className="summary-stats rescore-stats mt">
      <div>
        <b>{s.assessments}</b>
        <span>assessments move</span>
      </div>
      <div>
        <b>
          {s.down} / {s.up}
        </b>
        <span>score down / up</span>
      </div>
      <div>
        <b>{s.band_changes}</b>
        <span>change band</span>
      </div>
      <div>
        <b>{s.biggest_drop ? `−${s.biggest_drop}` : '0'}</b>
        <span>biggest drop</span>
      </div>
      <div>
        <b>{s.newly_capped}</b>
        <span>newly capped by a kill switch</span>
      </div>
      <div>
        <b>{signed(s.mean_change)}</b>
        <span>average change</span>
      </div>
    </div>
  );
}

function Items({ items }: { items: RescoreItem[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 25);
  return (
    <div className="table-wrap mt">
      <table className="grid">
        <thead>
          <tr>
            <th>Assessment</th>
            <th>Score</th>
            <th>Change</th>
            <th>Band</th>
            {items.some((i) => i.outcome) && <th>Outcome</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((i) => (
            <tr key={i.assessment_id}>
              <td>
                <a href={href.score(i.assessment_id)} className="cell-title">
                  {i.customer_name}
                </a>
              </td>
              <td className="num">
                {i.from_score ?? '–'} <ArrowRight size={12} /> {i.to_score}
                {i.to_cap !== null && i.from_cap === null && <span className="pill bad ml">capped</span>}
              </td>
              <td className="num">{signed(i.from_score === null ? null : i.to_score - i.from_score)}</td>
              <td>
                {i.from_band && i.from_band !== i.to_band ? (
                  <>
                    <span className={`pill ${bandTone(i.from_band)}`}>{bandLabel(i.from_band)}</span> <ArrowRight size={12} />{' '}
                  </>
                ) : null}
                <span className={`pill ${bandTone(i.to_band)}`}>{bandLabel(i.to_band)}</span>
              </td>
              {items.some((x) => x.outcome) && (
                <td className="small">
                  {i.outcome === 'skipped' ? <span title={i.skipped_reason ?? undefined}>Skipped: {i.skipped_reason}</span> : i.outcome === 'applied' ? 'Moved' : 'Planned'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && !all && (
        <button type="button" className="btn btn-sm btn-ghost mt" onClick={() => setAll(true)}>
          Show all {items.length}
        </button>
      )}
    </div>
  ); // prettier-ignore
}
